/* eslint-disable no-param-reassign */
import {
  createAsyncThunk, createSelector, createSlice, PayloadAction,
} from '@reduxjs/toolkit';
import {
  SEARCH_API, SEARCH_SIMILAR, THREAD_LISTING_API, THREAD_LISTING_V2_API,
} from 'src/constants/apiEndpoints';
import { MethodName } from 'src/constants/metrics';
import FetchStatus from 'src/types/FetchStatus';
import {
  Post, RequestType, SearchRequest, SearchResponse,
} from 'src/types/Search';
import { customFetch, Metrics } from 'src/utils';
import {
  CuminContentSearchQueryParams,
  CuminListingQueryParams,
  CuminSimilarDiscussionParams,
} from 'src/types/server/BaseRequest';
import qs from 'qs';
import { QueryParams } from 'src/types/forums-types';
import type { AppState } from '../store';
import { selectMarketplaceId } from './userInfo';

export enum SearchType {
  searchBar = 'searchBar',
  threadListing = 'threadListing',
  similarDiscussions = 'similarDiscussions',
  title = 'title',
  pinnedThreads = 'pinnedThreads',
}

export type SearchTypeState = {
  inputValue?: string;
  inputFocused?: boolean;
  status: FetchStatus;
  posts: Array<Post>;
  error?: string;
  cursor?: Array<string>;
  aggregations: { [aggregationName: string]: { [bucketKey: string]: number } },
  totalCount?: number;
  refresh?: boolean;
  ssrQueryParams?: QueryParams;
};

export type SearchState = {
  [SearchType.searchBar]: SearchTypeState;
  [SearchType.threadListing]: SearchTypeState & { showInputMinLength?: boolean }
  [SearchType.similarDiscussions]: SearchTypeState;
  [SearchType.title]: SearchTypeState;
  [SearchType.pinnedThreads]: SearchTypeState;
};

export const initialState: SearchState = {
  [SearchType.searchBar]: {
    inputValue: '',
    status: FetchStatus.idle,
    posts: [],
    cursor: [],
    aggregations: {},
    inputFocused: false,
  },
  [SearchType.threadListing]: {
    status: FetchStatus.idle,
    posts: [],
    cursor: [],
    aggregations: {},
    totalCount: 0,
    showInputMinLength: false,
  },
  [SearchType.similarDiscussions]: {
    status: FetchStatus.idle,
    posts: [],
    aggregations: {},
  },
  [SearchType.title]: {
    status: FetchStatus.idle,
    posts: [],
    aggregations: {},
  },
  [SearchType.pinnedThreads]: {
    status: FetchStatus.idle,
    posts: [],
    aggregations: {},
  },
};

const controllers: { [key in SearchType]?: AbortController } = {};
export const fetchSearchResults = createAsyncThunk(
  'postSearch/fetchSearchResults',
  async ({
    request, type, requestType = RequestType.search,
  }:
  { request: SearchRequest,
    type: SearchType,
    requestType?: RequestType,
  }, { rejectWithValue, getState }) => {
    if (controllers[type]) controllers[type]!.abort();
    controllers[type] = new AbortController();
    const apiEndpoint = requestType === RequestType.search ? SEARCH_API : THREAD_LISTING_API;
    return await customFetch(
      apiEndpoint,
      {
        method: 'POST',
        body: JSON.stringify({
          ...request,
          marketplaceId: selectMarketplaceId(getState() as AppState),
        }),
        signal: controllers[type]!.signal,
      },
      (error: Error) => {
        const { message, name } = error;
        return rejectWithValue({ message, name });
      },
      1,
    ) as SearchResponse;
  },
);

export type FetchSearchResultsV2Params = {
  type: SearchType.similarDiscussions | SearchType.title,
  request: CuminSimilarDiscussionParams
} | {
  type: SearchType.searchBar
  request: CuminContentSearchQueryParams
} | {
  type: SearchType.pinnedThreads,
  request: CuminListingQueryParams
} | {
  type: SearchType.threadListing,
  request: CuminListingQueryParams | CuminContentSearchQueryParams
};

const getSearchParams = (params: {
  type: SearchType,
  request: CuminSimilarDiscussionParams | CuminContentSearchQueryParams | CuminListingQueryParams,
}) => {
  const { type, request } = params;
  if (type === SearchType.similarDiscussions || type === SearchType.title) {
    return {
      apiEndpoint: SEARCH_SIMILAR,
      method: 'POST',
      body: JSON.stringify(request),
    };
  }

  return {
    apiEndpoint: `${THREAD_LISTING_V2_API}?${qs.stringify(request, {
      arrayFormat: 'comma',
      encode: true,
    })}`,
    method: 'GET',
  };
};

export const fetchSearchResultsV2 = createAsyncThunk(
  'postSearch/fetchSearchResultsV2',
  async ({ type, request }: FetchSearchResultsV2Params, { rejectWithValue }) => {
    if (controllers[type]) controllers[type]!.abort();
    controllers[type] = new AbortController();

    const { apiEndpoint, method, body } = getSearchParams({ type, request });
    return await customFetch(
      apiEndpoint,
      {
        method,
        ...(body && { body }),
        signal: controllers[type]!.signal,
      },
      (error: Error) => {
        const { message, name } = error;
        return rejectWithValue({ message, name });
      },
      1,
    ) as SearchResponse;
  },
);

function setThreadListings(
  payload: SearchResponse,
  state: SearchState,
  type: SearchType,
  lastEvaluatedKeys?: Array<string>,
) {
  // only update aggregations if there are new set of aggregations from API
  const { aggregations } = payload;

  if (aggregations && aggregations.length > 0) {
    state[type].aggregations = aggregations.reduce((aggPrev, aggregation) => {
      const { aggregationName, aggregationBuckets } = aggregation;
      return {
        ...aggPrev,
        [aggregationName]: aggregationBuckets.reduce((bucketPrev, bucket) => {
          const { bucketKey, count } = bucket;
          return {
            ...bucketPrev,
            [bucketKey]: count,
          };
        }, {}),
      };
    }, {});
  }

  let posts: Post[] = [];
  if (!lastEvaluatedKeys || lastEvaluatedKeys.length === 0) {
    posts = payload.posts;
  } else {
    posts = [...state[type].posts, ...payload.posts];
  }
  const uniquePostList = posts.filter(
    (post, index, postListArray) => index === postListArray.findIndex(
      (postListItem) => postListItem.postId === post.postId,
    ),
  );
  state[type].posts = uniquePostList;
  state[type].cursor = payload.lastEvaluatedKeys;
  state[type].status = FetchStatus.complete;
  state[type].totalCount = payload.totalCount;
  state[type].ssrQueryParams = payload.ssrQueryParams;
}

export const postSearch = createSlice({
  name: 'postSearch',
  initialState,
  reducers: {
    setInputValue: (
      state: SearchState,
      action: PayloadAction<{ inputValue: string, type: SearchType }>,
    ) => {
      const { inputValue: newInputValue, type } = action.payload;
      if (newInputValue.trim().length === 0) {
        state[type].posts = [];
      }
      state[type].inputValue = newInputValue;
    },
    setRefresh: (
      state: SearchState,
      action: PayloadAction<{ refresh: boolean, type: SearchType }>,
    ) => {
      const { refresh, type } = action.payload;
      state[type].refresh = refresh;
    },
    resetSearchType: (state, action: PayloadAction<SearchType>) => {
      state[action.payload] = initialState[action.payload];
    },
    setInputFocused: (
      state: SearchState,
      action: PayloadAction<{ focused: boolean, type: SearchType }>,
    ) => {
      const { focused, type } = action.payload;
      state[type].inputFocused = focused;
    },
    setListingShowInputMinLength: (
      state: SearchState,
      action: PayloadAction<boolean>,
    ) => {
      state[SearchType.threadListing].showInputMinLength = action.payload;
    },
    setSearchState: (
      state: SearchState,
      action: PayloadAction<{
        type: SearchType,
        response: SearchResponse,
        lastEvaluatedKeys?: Array<string>
      }>,
    ) => {
      const { type, response, lastEvaluatedKeys } = action.payload;
      setThreadListings(response, state, type, lastEvaluatedKeys);
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSearchResults
      .addCase(fetchSearchResults.rejected, (state: SearchState, action) => {
        const { message, name } = action.payload as { message: string, name: string };
        const { type } = action.meta.arg;
        state[type].error = message;
        if (name === 'AbortError' && !controllers[type]!.signal.aborted) {
          state[type].status = FetchStatus.loading;
        } else {
          state[type].status = FetchStatus.failed;
        }
      })
      .addCase(fetchSearchResults.pending, (state: SearchState, action) => {
        const { type, request } = action.meta.arg;
        state[type].status = FetchStatus.loading;
        state[type].refresh = false;
        if (!request.lastEvaluatedKeys || request.lastEvaluatedKeys.length === 0) {
          state[type].posts = [];
        }
      })
      .addCase(fetchSearchResults.fulfilled, (state: SearchState, action) => {
        const { type, request } = action.meta.arg;

        const searchMetric = new Metrics(`${MethodName.searchRequests}.${type}`);
        searchMetric.publishCounterMetric('results', action.payload.posts.length);

        setThreadListings(action.payload, state, type, request.lastEvaluatedKeys);
      })
      // fetchSearchResultsV2
      .addCase(fetchSearchResultsV2.rejected, (state: SearchState, action) => {
        const { message, name } = action.payload as { message: string, name: string };
        const { type } = action.meta.arg;
        state[type].error = message;
        if (name === 'AbortError' && !controllers[type]!.signal.aborted) {
          state[type].status = FetchStatus.loading;
        } else {
          state[type].status = FetchStatus.failed;
        }
      })
      .addCase(fetchSearchResultsV2.pending, (state: SearchState, action) => {
        const { type, request: { lastEvaluatedKeys } } = action.meta.arg;
        state[type].status = FetchStatus.loading;
        state[type].refresh = false;
        if (!lastEvaluatedKeys || lastEvaluatedKeys.length === 0) {
          state[type].posts = [];
        }
      })
      .addCase(fetchSearchResultsV2.fulfilled, (state: SearchState, action) => {
        const { type, request: { lastEvaluatedKeys } } = action.meta.arg;
        const searchMetric = new Metrics(`${MethodName.searchRequests}.${type}`);
        searchMetric.publishCounterMetric('results', action.payload.posts.length);

        setThreadListings(action.payload, state, type, lastEvaluatedKeys);
      });
  },
});

export const {
  setInputValue, resetSearchType, setRefresh, setInputFocused,
  setListingShowInputMinLength, setSearchState,
} = postSearch.actions;

export default postSearch.reducer;

// Search Selectors
export const selectSearch = createSelector(
  [
    (state: AppState) => state.postSearch,
    (state: AppState, type: SearchType) => type,
  ],
  (searchBaseSlice, type) => (searchBaseSlice[type]),
);
